<template>
  <label class="switch" :title="isDarkMode ? 'Light mode' : 'Dark mode'">
    <span class="sun">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <g fill="#f6c945">
          <circle r="5" cy="12" cx="12" />
          <path
            d="m21 13h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 0 2zm-17 0h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 0 2zm13.66-5.66a1 1 0 0 1 -.66-.29 1 1 0 0 1 0-1.41l.71-.71a1 1 0 1 1 1.41 1.41l-.71.71a1 1 0 0 1 -.75.29zm-12.02 12.02a1 1 0 0 1 -.71-.29 1 1 0 0 1 0-1.41l.71-.66a1 1 0 0 1 1.41 1.41l-.71.71a1 1 0 0 1 -.7.24zm6.36-14.36a1 1 0 0 1 -1-1v-1a1 1 0 0 1 2 0v1a1 1 0 0 1 -1 1zm0 17a1 1 0 0 1 -1-1v-1a1 1 0 0 1 2 0v1a1 1 0 0 1 -1 1zm-5.66-14.66a1 1 0 0 1 -.7-.29l-.71-.71a1 1 0 0 1 1.41-1.41l.71.71a1 1 0 0 1 0 1.41 1 1 0 0 1 -.71.29zm12.02 12.02a1 1 0 0 1 -.7-.29l-.66-.71a1 1 0 0 1 1.36-1.36l.71.71a1 1 0 0 1 0 1.41 1 1 0 0 1 -.71.24z"
          />
        </g>
      </svg>
    </span>
    <span class="moon">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
        <path
          d="m223.5 32c-123.5 0-223.5 100.3-223.5 224s100 224 223.5 224c60.6 0 115.5-24.2 155.8-63.4 5-4.9 6.3-12.5 3.1-18.7s-10.1-9.7-17-8.5c-9.8 1.7-19.8 2.6-30.1 2.6-96.9 0-175.5-78.8-175.5-176 0-65.8 36-123.1 89.3-153.3 6.1-3.5 9.2-10.5 7.7-17.3s-7.3-11.9-14.3-12.5c-6.3-.5-12.6-.8-19-.8z"
        />
      </svg>
    </span>
    <input v-model="isDarkMode" type="checkbox" class="input">
    <span class="slider" />
  </label>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useColors } from 'vuestic-ui'

const getStorage = (): Storage => {
  if (sessionStorage.getItem('impersonation_session')) {
    return sessionStorage
  }

  if (sessionStorage.getItem('admin_token') && sessionStorage.getItem('role') === 'agent') {
    return sessionStorage
  }

  return localStorage
}

const { applyPreset, currentPresetName } = useColors()
const theme = computed({
  get: () => currentPresetName.value,
  set: (v) => {
    applyPreset(v)
    if (v === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    const storage = getStorage()
    storage.setItem('app_local_theme', v)
    window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: v } }))
  },
})

const isDarkMode = computed({
  get: () => theme.value === 'dark',
  set: (checked: boolean) => {
    theme.value = checked ? 'dark' : 'light'
  },
})

const initializeTheme = () => {
  const storage = getStorage()
  const storedTheme = storage.getItem('app_local_theme')
  const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  theme.value = storedTheme || systemTheme
}

watch(
  () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  (isDark) => {
    const storage = getStorage()
    if (!storage.getItem('app_local_theme')) {
      theme.value = isDark ? 'dark' : 'light'
    }
  },
)

initializeTheme()
</script>

<style scoped>
.switch {
  position: relative;
  display: inline-block;
  width: 54px;
  height: 30px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: linear-gradient(135deg, #89c4f0 0%, #5aa9e6 100%);
  transition: background 0.28s ease, box-shadow 0.2s ease;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, #4f97d8 40%, transparent);
  box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.25);
}

.slider:before {
  position: absolute;
  content: "";
  height: 24px;
  width: 24px;
  border-radius: 50%;
  left: 2px;
  bottom: 2px;
  z-index: 2;
  background: linear-gradient(180deg, #ffffff 0%, #eef2f7 100%);
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.22);
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.sun svg {
  position: absolute;
  top: 6px;
  left: 31px;
  z-index: 1;
  width: 16px;
  height: 16px;
  animation: rotate 16s linear infinite;
}

.moon svg {
  fill: #9ec9ef;
  position: absolute;
  top: 6px;
  left: 7px;
  z-index: 1;
  width: 15px;
  height: 15px;
  animation: tilt 5.5s ease-in-out infinite;
}

@keyframes rotate {
  from { transform: rotate(0); }
  to { transform: rotate(360deg); }
}

@keyframes tilt {
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(-8deg); }
}

.input:checked + .slider {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-color: color-mix(in srgb, #334155 70%, transparent);
}

.input:focus-visible + .slider {
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--va-primary) 28%, transparent);
}

.input:checked + .slider:before {
  transform: translateX(24px);
  background: linear-gradient(180deg, #e2e8f0 0%, #cbd5e1 100%);
}

@media (max-width: 480px) {
  .switch {
    width: 48px;
    height: 28px;
  }

  .slider:before {
    width: 22px;
    height: 22px;
  }

  .sun svg,
  .moon svg {
    width: 14px;
    height: 14px;
  }

  .sun svg { left: 28px; top: 6px; }
  .moon svg { left: 6px; top: 6px; }

  .input:checked + .slider:before {
    transform: translateX(20px);
  }
}
</style>
